<template>
  <div class="base-chart" :key="id">
    <div :style="`min-height: ${gridStepHeight}px`" :id="id" class="base-chart__chart-container"></div>
  </div>
</template>

<script lang="ts">
import { Root } from "@amcharts/amcharts5";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Dark from "@amcharts/amcharts5/themes/Dark";
import { cloneDeep, isEqual } from "lodash";
import { v4 as uuid } from "uuid";
import Vue from "vue";
import { ThemeOptions } from "vuetify/types/services/theme";
import { mapGetters, mapMutations } from "vuex";

import BaseChartClass, { Options } from "@/classes/BaseChart";
import { EXPLORE_MODULE } from "@/constants";
import chartComponentsConfig from "@/settings/chartComponentConfigs";
import { gridStepHeight } from "@/settings/constants";
import { ContentWidget } from "@/types/layouts";

export interface BaseChartData {
  configLoaded: boolean;
  showSettingsDialog: boolean;
  theme: ThemeOptions;
  id: string;
  widget: ContentWidget;
  chartName: string;
  amChartRoot: Root;
  chartObject: BaseChartClass;
  gridStepHeight?: number;
  moduleName: string;
}

type ChartClassConstructor = new (
  widget: ContentWidget,
  options?: Options,
  dataProvider?: (chart: BaseChartClass) => Promise<any>,
  module?: string
) => BaseChartClass;

export default Vue.extend({
  name: "BaseChart",

  props: {
    chart: Object,
    chartType: String,
    dataProvider: Function,
    module: {
      type: String,
      default: EXPLORE_MODULE,
    },
    keepApiOrder: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      configLoaded: false,
      showSettingsDialog: false,
      theme: {} as ThemeOptions,
      id: uuid(),
      moduleName: this.module,
    } as BaseChartData;
  },

  watch: {
    filterParams: {
      deep: true,
      async handler(): Promise<void> {
        await this.refreshChartData();
      },
    },

    chart: {
      deep: true,
      async handler(newValue: ContentWidget, oldValue: ContentWidget): Promise<void> {
        this.widget = { ...newValue, settings: { ...(newValue.settings || {}), keepApiOrder: !!this.keepApiOrder } };

        if (!isEqual(oldValue.settings, newValue.settings) || !isEqual(oldValue.controls, newValue.controls)) {
          await this.refreshChartData();
        }
      },
    },

    chartType: {
      deep: true,
      handler(oldValue: string, newValue: string) {
        this.chartName = newValue;
      },
    },
  },

  computed: {
    ...mapGetters("userSettings", ["filterParams"]),
  },

  async mounted() {
    let chartObject = {} as BaseChartClass;
    let widget = {} as ContentWidget;
    let chartName = "";
    chartName = this.chartType;
    widget = cloneDeep(this.chart);
    widget.settings = { ...(widget.settings || {}), keepApiOrder: !!this.keepApiOrder };
    const chartConfig = chartComponentsConfig[chartName];
    this.theme = this.$vuetify.theme;

    if (chartConfig) {
      const { chartClass, chartOptions } = chartConfig;
      if (this.dataProvider) {
        const dataProvider = this.dataProvider as (chart: BaseChartClass) => Promise<unknown>;
        chartObject = new (chartClass as ChartClassConstructor)(widget, chartOptions, dataProvider, this.module);
      } else {
        chartObject = new (chartClass as ChartClassConstructor)(widget, chartOptions, undefined, this.module);
      }
      chartObject.loadChart(chartName, widget?.family);
      this.chartObject = chartObject;
      this.widget = widget;
      this.chartName = chartName;
      await this.createChart();
      this.$emit("updateChartObject", chartObject);
    }
  },

  beforeMount() {
    this.gridStepHeight = gridStepHeight;
  },

  beforeDestroy() {
    this.amChartRoot.dispose();
    this.amChartRoot = {} as Root;
  },

  methods: {
    ...mapMutations("chart", ["saveConfig"]),

    async refreshChartData(): Promise<void> {
      if (typeof this.chartObject.refreshData === "function") {
        try {
          this.$emit("widgetLoading", true);

          const w = {
            ...this.widget,
            settings: { ...(this.widget?.settings || {}), keepApiOrder: !!this.keepApiOrder },
          };
          await this.chartObject.refreshData(w as ContentWidget);
        } catch (e) {
          console.error("Error refresh chart data:", e);
        } finally {
          this.$emit("widgetLoading", false);
        }
      }
    },

    async createChart(): Promise<void> {
      let amChartRoot = {} as Root;
      amChartRoot = Root.new(this.id, {
        useSafeResolution: false,
      });

      if (this.theme.dark) {
        amChartRoot.setThemes([am5themes_Animated.new(amChartRoot), am5themes_Dark.new(amChartRoot)]);
      } else {
        amChartRoot.setThemes([am5themes_Animated.new(amChartRoot)]);
      }

      this.amChartRoot = amChartRoot;

      try {
        this.$emit("widgetLoading", true);
        if (typeof this.chartObject?.create === "function") {
          await this.chartObject?.create(amChartRoot);
        }
      } catch (e) {
        console.error("Error create chart:", e);
      } finally {
        this.$emit("widgetLoading", false);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.base-chart {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-grow: 1;
  overflow: hidden;
  padding-bottom: 10px; // We need this padding to make pdf2html export content correctly.

  &__chart-container {
    display: block;
    height: 100%;
    width: 100%;
  }
}
</style>
